import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useV2, isV2Path } from "../context/V2Context";
import { NewPill } from "./NewPill";
import ComingSoon from "../pages/ComingSoon";
import { api } from "../lib/api";
import { AlertsBell } from "./AlertsBell";
import { SessionTimeout } from "./SessionTimeout";
import { AccessibilityWidget } from "./AccessibilityWidget";
import {
  LayoutDashboard, Users, ClipboardList, CalendarDays, Building2, UserRound,
  Briefcase, ListChecks, Globe, UsersRound, BarChart3, BookOpen, Video,
  MessageSquare, CheckSquare, Calendar, LogOut, ShieldCheck, Menu, Settings2, ChevronDown, ChevronRight, Landmark, Gauge, Printer, FileText, Sparkles, HelpCircle, Search as SearchIcon
} from "lucide-react";
import { PanelLeftClose, PanelLeftOpen, AlarmClock, FileSignature, PenLine, FileWarning, ShieldAlert, Unlock, Wand2, PhoneCall, BellRing } from "lucide-react";
import { GlobalSearch } from "./GlobalSearch";
import { HeaderClock } from "./HeaderClock";
import { QuickAdd } from "./QuickAdd";
import { ProfileMenu } from "./ProfileMenu";
import { AppFooter } from "./Footer";
import ReleaseRibbon from "./ReleaseRibbon";

function SoonPill({ dark }) {
  return (
    <span className={`ml-auto inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${dark ? "bg-[#FFB300]/20 text-[#FFB300]" : "bg-[#FFB300] text-[#3E2723]"}`}>
      <Sparkles size={9} /> V2
    </span>
  );
}

const TOP_MODULES = [
  { label: "Clinical Forms", to: "/clinical-forms" },
  { label: "Clinical Outcome Measures", to: "/outcome-measures" }, { label: "Clinical Reports", to: "/reports" },
  { label: "Letters & Messaging", to: "/letters" }, { label: "ASAM", to: "/asam" }, { label: "Behavior Tracker", to: "/behavior-tracker" },
  { label: "Quality Measures", to: "/quality-measures" }, { label: "Print", to: "/print" },
];

const EO_ITEMS = [
  { label: "Referral Form", to: "/engagement/referrals" },
  { label: "Intake Tasks", to: "/engagement/intake-tasks" },
  { label: "Intake/Add New Client", to: "/engagement/intake" },
  { label: "Insurance Verification", to: "/engagement/insurance-verification" },
  { label: "Consent Forms", to: "/engagement/consent-forms", arrow: true },
  { label: "Orientation Checklist", to: "/engagement/orientation-checklist" },
  { label: "Client Entrance Survey", to: "/engagement/entrance-survey" },
];

const SIDE_GROUPS = [
  { title: "Overview", items: [
    { label: "Dashboard", to: "/", icon: LayoutDashboard },
    { label: "Census", to: "/census", icon: ClipboardList },
    { label: "Reports", to: "/reports", icon: BarChart3 },
    { label: "Quality Measures", to: "/quality-measures", icon: Gauge },
    { label: "Overdue Center", to: "/overdue", icon: AlarmClock },
    { label: "Chart Gaps", to: "/chart-gaps", icon: FileWarning },
  ]},
  { title: "Client Care", items: [
    { label: "Clients", to: "/clients", icon: Users },
    { label: "Complete Chart", to: "/clinical/documents", icon: FileText },
    { label: "Caseload", to: "/caseload", icon: Briefcase },
    { label: "Prescriber Dashboard", to: "/prescriber-dashboard", icon: FileSignature, prescriberOnly: true, fresh: "prescriber-dashboard" },
    { label: "Refill Call Queue", to: "/refill-call-queue", icon: PhoneCall, roles: ["admin", "clinician"], fresh: "refill-call-queue" },
    { label: "Handout Reminders", to: "/handout-reminders", icon: BellRing, roles: ["admin", "clinician"], fresh: "handout-reminders" },
    { label: "Groups", to: "/groups", icon: UsersRound },
  ]},
  { title: "Operations", items: [
    { label: "Scheduler", to: "/scheduler", icon: CalendarDays },
    { label: "Order Sign-Off", to: "/orders/sign-off", icon: PenLine, roles: ["admin", "clinician"] },
    { label: "Unlock Requests", to: "/admin/unlock-requests", icon: Unlock },
    { label: "Requests", to: "/requests", icon: ListChecks },
    { label: "Tasks", to: "/tasks", icon: ListChecks },
    { label: "Equipment", to: "/equipment", icon: Building2 },
    { label: "Letters & Messaging", to: "/letters", icon: MessageSquare },
    { label: "Print Center", to: "/print", icon: Printer },
    { label: "External Entities", to: "/external-entities", icon: Globe },
    { label: "Insurance Companies", to: "/insurance-companies", icon: Building2 },
    { label: "Revenue Cycle", to: "/revenue", icon: Landmark },
    { label: "Telehealth Videoing", to: "/telehealth", icon: Video },
  ]},
  { title: "Training & Resources", items: [
    { label: "Education Resources", to: "/resources", icon: BookOpen },
  ]},
  { title: "Support", items: [
    { label: "Help & Knowledge Base", to: "/help", icon: HelpCircle },
  ]},
];

const ROLE_LABELS = { admin: "Administrator", clinician: "Clinician", front_desk: "Front Desk", billing: "Billing" };

const PAGE_TITLES = [
  ["/clinical/documents", "Complete Chart"], ["/clinical/progress-notes", "Progress Notes"],
  ["/clinical/treatment-plans", "Treatment Plans"], ["/clinical/assessments", "Assessments"],
  ["/clinical/consents", "Consent Forms"], ["/clinical/crisis-plans", "Crisis or Safety Plans"],
  ["/clinical", "Complete Chart"],
  ["/engagement/referrals", "Referrals"], ["/engagement/intake", "Intake"], ["/engagement", "Engagement & Onboarding"],
  ["/outcome-measures", "Clinical Outcome Measures"], ["/settings", "Settings"], ["/platform", "Platform Console"],
  ["/users", "Users"], ["/security", "Security & 2FA"], ["/configuration", "Data & Configuration"],
  ["/asam", "ASAM"], ["/behavior-tracker", "Behavior Tracker"], ["/revenue", "Revenue Cycle"],
  ["/module", "Module"], ["/legal", "Legal & Compliance"], ["/help", "Help & Knowledge Base"],
  ...TOP_MODULES.filter((m) => m.to).map((m) => [m.to, m.label]),
  ...SIDE_GROUPS.flatMap((g) => g.items.map((i) => [i.to, i.label])).filter(([to]) => to !== "/"),
];

export function pageTitle(pathname) {
  if (pathname === "/") return "Dashboard";
  const hit = PAGE_TITLES.filter(([p]) => pathname === p || pathname.startsWith(p + "/")).sort((a, b) => b[0].length - a[0].length)[0];
  return hit ? hit[1] : "";
}

const NAV_MODULE = { "/": "dashboard", "/reports": "reports", "/quality-measures": "reports", "/clients": "clients", "/census": "clients",
  "/caseload": "clients", "/groups": "groups", "/clinical-forms": "forms", "/scheduler": "scheduler", "/tasks": "tasks",
  "/letters": "letters", "/print": "print", "/insurance-companies": "billing", "/revenue": "billing" };

export default function Layout() {
  const { user, logout } = useAuth();
  const { v2Enabled } = useV2();
  const navigate = useNavigate();
  const location = useLocation();
  const curtained = (to) => v2Enabled === false && isV2Path(to);
  useEffect(() => {
    const p = pageTitle(location.pathname);
    document.title = p ? `${p} | COCOA EHR` : "COCOA EHR";
  }, [location.pathname]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cocoa_side_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("cocoa_side_collapsed", c ? "0" : "1"); return !c; });
  const [eoOpen, setEoOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const eoRef = useRef(null);
  const moreRef = useRef(null);
  const [sideTip, setSideTip] = useState(null);
  const [perms, setPerms] = useState(null);
  const [consoleReturn, setConsoleReturn] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("console_return")); } catch { return null; }
  });
  useEffect(() => { api.get("/me/permissions").then((r) => setPerms(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/branding").then((r) => sessionStorage.setItem("org_branding", JSON.stringify(r.data))).catch(() => {}); }, []);
  useEffect(() => {
    if (!eoOpen) return;
    const h = (e) => { if (eoRef.current && !eoRef.current.contains(e.target)) setEoOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [eoOpen]);
  useEffect(() => {
    if (!moreOpen) return;
    const h = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [moreOpen]);
  useEffect(() => {
    const sync = () => { try { setConsoleReturn(JSON.parse(sessionStorage.getItem("console_return"))); } catch { setConsoleReturn(null); } };
    window.addEventListener("focus", sync);
    const t = setInterval(sync, 1500);
    return () => { window.removeEventListener("focus", sync); clearInterval(t); };
  }, []);
  const canView = (to) => { const mod = NAV_MODULE[to]; return !perms || !mod || perms[mod]?.view !== false; };
  const tipHandlers = (label) => collapsed ? {
    onMouseEnter: (e) => setSideTip({ label, top: e.currentTarget.getBoundingClientRect().top }),
    onMouseLeave: () => setSideTip(null),
  } : {};

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <SessionTimeout />
      <AccessibilityWidget />
      <header className="sticky top-0 z-50 bg-[#3E2723] text-white shadow-sm">
        <div className="flex h-20 items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-sidebar-toggle" onClick={() => setSideOpen(!sideOpen)} aria-label="Toggle sidebar" className="md:hidden p-2 rounded-full hover:bg-white/10">
              <Menu size={20} />
            </button>
            <img src="/logos/cocoa_ehr_logo_white.webp" alt="Cocoa EHR" className="h-20 w-auto" data-testid="header-logo" />
          </div>
          <div className="hidden md:flex flex-1 justify-center px-4">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1">
            <HeaderClock />
            <QuickAdd />
            <button data-testid="header-mobile-search-btn" aria-label="Search" onClick={() => setMobileSearch((s) => !s)} className="md:hidden p-2 rounded-full hover:bg-white/10 transition-colors"><SearchIcon size={18} /></button>
            <button data-testid="header-calendar-btn" aria-label="Calendar" onClick={() => navigate("/scheduler")} className="hidden sm:block p-2 rounded-full hover:bg-white/10 transition-colors"><Calendar size={18} /></button>
            <button data-testid="header-tasks-btn" aria-label="Tasks" onClick={() => navigate("/tasks")} className="hidden sm:block p-2 rounded-full hover:bg-white/10 transition-colors"><CheckSquare size={18} /></button>
            <button data-testid="header-help-btn" aria-label="Help & Knowledge Base" title="Help & Knowledge Base" onClick={() => navigate("/help")} className="hidden sm:block p-2 rounded-full hover:bg-white/10 transition-colors"><HelpCircle size={18} /></button>
            <AlertsBell />
            <ProfileMenu />
          </div>
        </div>
        {mobileSearch && (
          <div className="md:hidden px-4 pb-3" data-testid="mobile-search-row">
            <GlobalSearch mobile />
          </div>
        )}
        <nav className="hidden lg:flex items-center gap-1 px-6 pt-2 pb-3 overflow-visible" data-testid="top-module-nav">
          <div className="relative" ref={eoRef}>
            <button data-testid="top-nav-engagement-onboarding" onClick={() => setEoOpen(!eoOpen)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${eoOpen ? "bg-white/15 text-white" : "text-white/70 hover:text-white hover:bg-white/10"}`}>
              Engagement & Onboarding <ChevronDown size={13} className={`transition-transform ${eoOpen ? "rotate-180" : ""}`} />
            </button>
            {eoOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 rounded-2xl bg-white text-[#3E2723] shadow-[0_12px_40px_-8px_rgba(62,39,35,0.35)] border border-[#EFEBE0] py-3 z-[70]" data-testid="engagement-dropdown">
                {EO_ITEMS.map((item) => (
                  <button key={item.label} data-testid={`eo-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => { setEoOpen(false); navigate(item.to); }}
                    className="flex w-full items-center justify-between px-5 py-2.5 text-sm hover:bg-[#F5F2EB] transition-colors">
                    {item.label}
                    {item.arrow && <ChevronRight size={14} className="text-[#84675B]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          {TOP_MODULES.map((m) => {
            const isSoon = curtained(m.to);
            return (
              <button key={m.label} data-testid={`top-nav-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => navigate(m.to || `/module/${encodeURIComponent(m.label)}`)}
                className={`items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors ${isSoon ? "hidden xl:inline-flex" : "inline-flex"}`}>
                {m.label}{isSoon && <SoonPill dark />}
              </button>
            );
          })}
          {TOP_MODULES.some((m) => curtained(m.to)) && (
            <div className="relative xl:hidden" ref={moreRef}>
              <button data-testid="top-nav-more" onClick={() => setMoreOpen(!moreOpen)}
                className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${moreOpen ? "bg-white/15 text-white" : "text-white/70 hover:text-white hover:bg-white/10"}`}>
                More <ChevronDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl bg-white text-[#3E2723] shadow-[0_12px_40px_-8px_rgba(62,39,35,0.35)] border border-[#EFEBE0] py-2 z-[70]" data-testid="top-nav-more-dropdown">
                  {TOP_MODULES.filter((m) => curtained(m.to)).map((m) => (
                    <button key={m.label} data-testid={`top-nav-more-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => { setMoreOpen(false); navigate(m.to || `/module/${encodeURIComponent(m.label)}`); }}
                      className="flex w-full items-center justify-between gap-3 px-5 py-2 text-sm hover:bg-[#F5F2EB] transition-colors">
                      <span className="truncate">{m.label}</span>
                      <SoonPill dark />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
      </header>

      <div className="flex">
        <aside className={`${sideOpen ? "flex" : "hidden"} md:flex fixed md:sticky top-[124px] md:top-[124px] z-40 h-[calc(100vh-124px)] ${collapsed ? "md:w-[64px]" : "w-60"} flex-col bg-[#F5F2EB] border-r border-[#EFEBE0] overflow-y-auto transition-[width] duration-200`} data-testid="sidebar-nav">
          <div className={`hidden md:flex ${collapsed ? "justify-center" : "justify-end"} px-2 pt-3`}>
            <button data-testid="sidebar-collapse-toggle" aria-label={collapsed ? "Expand navigation" : "Collapse navigation"} onClick={toggleCollapsed}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-[#7B6157] hover:bg-[#EDE7DC] transition-colors">
              {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </div>
          <nav className="flex flex-col px-2 pb-6">
            {SIDE_GROUPS.map((group) => (
              <div key={group.title}>
                <div className={`px-2 pt-5 pb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#84675B] ${collapsed ? "md:hidden" : ""}`} data-testid={`side-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {group.title}
                </div>
                {collapsed && <div className="hidden md:block mx-2 mt-4 border-t border-[#E5DFD3]" />}
                <div className="flex flex-col gap-0.5">
                  {group.items.filter(({ to, roles, prescriberOnly }) => canView(to) && (!roles || user?.is_super_admin || roles.includes(user?.role)) && (!prescriberOnly || user?.is_prescriber)).map(({ label, to, icon: Icon, fresh }) => (
                    <NavLink key={to} to={to} end={to === "/"} onClick={() => { setSideOpen(false); setSideTip(null); }} {...tipHandlers(label)}
                      data-testid={`side-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${collapsed ? "md:justify-center md:px-0" : ""} ${
                          isActive ? "bg-[#4E342E] text-white shadow-[0_4px_12px_rgba(78,52,46,0.2)]" : "text-[#5D4037] hover:bg-[#EDE7DC]"
                        }`}>
                      <Icon size={17} className="shrink-0" /> <span className={`flex flex-1 items-center gap-1.5 ${collapsed ? "md:hidden" : ""}`}>{label}{curtained(to) && <SoonPill />}{fresh && <NewPill feature={fresh} className="ml-auto" />}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
            {(user?.role === "admin" || user?.is_super_admin) && (
              <div>
                <div className={`px-2 pt-5 pb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#84675B] ${collapsed ? "md:hidden" : ""}`} data-testid="side-group-administration">
                  Administration
                </div>
                {collapsed && <div className="hidden md:block mx-2 mt-4 border-t border-[#E5DFD3]" />}
                <div className="flex flex-col gap-0.5">
                  {[...(user?.role === "admin" ? [
                    { label: "Settings", to: "/settings", icon: Settings2 },
                  ] : []),
                  ...(user?.is_super_admin ? [{ label: "Platform Console", to: "/platform", icon: Landmark }] : []),
                  ].map(({ label, to, icon: Icon }) => (
                    <NavLink key={to} to={to} onClick={() => { setSideOpen(false); setSideTip(null); }} {...tipHandlers(label)}
                      data-testid={`side-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${collapsed ? "md:justify-center md:px-0" : ""} ${
                          isActive ? "bg-[#4E342E] text-white shadow-[0_4px_12px_rgba(78,52,46,0.2)]" : "text-[#5D4037] hover:bg-[#EDE7DC]"
                        }`}>
                      <Icon size={17} className="shrink-0" /> <span className={collapsed ? "md:hidden" : ""}>{label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </nav>
        </aside>
        {collapsed && sideTip && (
          <div className="pointer-events-none fixed z-[95] hidden md:block rounded-lg bg-[#3E2723] px-2.5 py-1.5 text-xs font-semibold text-white shadow-xl whitespace-nowrap"
            style={{ left: 70, top: sideTip.top + 6 }} data-testid="side-nav-tooltip">
            {sideTip.label}
          </div>
        )}
        <main id="main-content" className="flex-1 min-w-0 p-5 lg:p-8">
          <ReleaseRibbon />
          {curtained(location.pathname) ? <ComingSoon /> : v2Enabled === null && isV2Path(location.pathname) ? null : <Outlet />}
        </main>
      </div>
      <AppFooter />
      {consoleReturn && (
        <div className="fixed bottom-5 left-5 z-[80] flex items-center gap-1 rounded-full bg-[#3E2723] text-white shadow-[0_8px_28px_rgba(62,39,35,0.4)] pl-4 pr-1.5 py-1.5" data-testid="console-return-chip">
          <button data-testid="console-return-btn" onClick={() => navigate(`/clients/${consoleReturn.id}/console`)}
            className="text-xs font-semibold hover:underline">← Back to {consoleReturn.name}&apos;s console</button>
          <button data-testid="console-return-dismiss" aria-label="Dismiss"
            onClick={() => { sessionStorage.removeItem("console_return"); setConsoleReturn(null); }}
            className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/15 text-white/70">×</button>
        </div>
      )}
    </div>
  );
}
